import React, { Component } from 'react'
import { destroy, get, post, put } from './util'
import Panel from './components/Panel'
import Button from './components/Button'
import EditUser from './EditUser'
import AddUser from './AddUser'
import ImportCSV from './ImportCSV'
import Modal from './components/Modal'

class User extends Component {
  state = {
    loading: true,
    users: [],
    addOpen: false,
    edit: null,
    csv: null,
    remove: null,
    notice: null,
    activeId: null
  }

  componentDidMount () {
    get('admin/users').then(users => {
      this.setState({ users, loading: false })
    })
  }

  showMessage = message => {
    this.setState({ notice: message })
    setTimeout(() => this.setState({ notice: null }), 4000)
  }

  addUser = user => {
    if (!user) {
      this.showMessage('Username should not be empty')
      return
    }
    post('admin/users', { user })
      .then(newUser => {
        newUser.deviceCount = 0
        this.setState({ addOpen: false, users: this.state.users.concat(newUser) })
        this.showMessage('Created successfully')
      })
      .catch(e => {
        this.showMessage(`Create failed: ${e.message}`)
      })
  }

  renderTable () {
    if (!this.state.users.length) {
      return (
        <Panel>
          <div className='text-center'>
            <h3 className='text-lg font-semibold text-ink'>No users</h3>
            <p className='text-sm text-slate-500 mt-2'>
              Invite your teammates and assign device quota here.
            </p>
          </div>
        </Panel>
      )
    }

    return (
      <Panel tight>
        <div className='overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='text-slate-500 border-b border-slate-100'>
              <tr className='text-left'>
                <th className='px-3 py-2'>User</th>
                <th className='px-3 py-2'>Used Quota</th>
                <th className='px-3 py-2'>Device Quota</th>
                <th className='px-3 py-2'>Passcode</th>
                <th className='px-3 py-2 text-right'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {this.state.users.map(user => (
                <tr
                  key={user.id}
                  className='border-b border-slate-50 hover:bg-slate-50/60'
                  onMouseEnter={() => this.setState({ activeId: user.id })}
                  onMouseLeave={() => this.setState({ activeId: null })}
                >
                  <td className='px-3 py-2 font-semibold text-ink'>{user.user}</td>
                  <td className='px-3 py-2'>{user.deviceCount}</td>
                  <td className='px-3 py-2 text-slate-500'>
                    {user.quota || this.props.company.defaultQuotaPerUser}
                  </td>
                  <td className='px-3 py-2 font-mono text-sm'>
                    {this.state.activeId === user.id ? user.passcode : user.passcode.replace(/./g, '*')}
                  </td>
                  <td className='px-3 py-2 text-right space-x-3'>
                    <button className='text-blue-600 font-semibold' onClick={() => this.setState({ edit: user })}>
                      Edit
                    </button>
                    <button className='text-rose-500 font-semibold' onClick={() => this.setState({ remove: user })}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    )
  }

  render () {
    if (this.state.loading) {
      return <div className='flex items-center justify-center py-20 text-slate-500'>Loading users…</div>
    }

    return (
      <div className='space-y-4'>
        {this.state.notice && (
          <div className='rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex justify-between items-center'>
            <span>{this.state.notice}</span>
            <button className='font-semibold' onClick={() => this.setState({ notice: null })}>Close</button>
          </div>
        )}
        <Panel tight>
          <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
            <div>
              <h2 className='text-xl font-semibold text-ink'>Users</h2>
              <p className='text-sm text-slate-500'>Manage licenses, reset passcodes, and import accounts from CSV.</p>
            </div>
            <div className='flex flex-wrap gap-2'>
              <Button variant='subtle' onClick={() => this.setState({ csv: true })}>
                Import CSV
              </Button>
              <Button onClick={() => this.setState({ addOpen: true })}>Create User</Button>
            </div>
          </div>
        </Panel>
        {this.renderTable()}

        <Modal
          open={Boolean(this.state.remove)}
          onClose={() => this.setState({ remove: null })}
          title='Remove user'
          footer={[
            <Button key='cancel' variant='ghost' onClick={() => this.setState({ remove: null })}>Cancel</Button>,
            <Button
              key='remove'
              onClick={() => {
                const { id } = this.state.remove
                destroy(`admin/users/${id}`)
                  .then(() => {
                    this.setState({
                      remove: null,
                      users: this.state.users.filter(user => user.id !== id)
                    })
                    this.showMessage('Removed successfully')
                  })
                  .catch(e => {
                    this.setState({ remove: null })
                    this.showMessage(`Remove failed: ${e.message}`)
                  })
              }}
            >
              Remove
            </Button>
          ]}
        >
          <p>
            Are you sure you want to delete{' '}
            <strong>{(this.state.remove && this.state.remove.user) || 'this user'}</strong>? Their
            devices will be unregistered and the action cannot be undone.
          </p>
        </Modal>

        <ImportCSV
          show={this.state.csv}
          defaultQuota={this.props.company.defaultQuotaPerUser}
          onClose={() => this.setState({ csv: null })}
          onSave={() => {
            this.setState({ loading: true, csv: null })
            get('admin/users').then(users => {
              this.setState({ users, loading: false })
              this.showMessage('Imported successfully')
            })
          }}
        />
        <AddUser
          show={this.state.addOpen}
          onClose={() => this.setState({ addOpen: false })}
          onSave={this.addUser}
        />
        <EditUser
          company={this.props.company}
          data={this.state.edit}
          onClose={() => this.setState({ edit: null })}
          onSave={(data, { id }) => {
            put(`admin/users/${id}`, data)
              .then(() => {
                this.setState({
                  edit: null,
                  users: this.state.users.map(user => (user.id === id ? { ...user, ...data } : user))
                })
                this.showMessage('Updated successfully')
              })
              .catch(e => {
                this.setState({ edit: null })
                this.showMessage(`Update failed: ${e.message}`)
              })
          }}
        />
      </div>
    )
  }
}

export default User
